import {
    Box,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Typography,
    OutlinedInput,
    IconButton,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import { UserNotification } from 'types/notification/notification';
import { useRuleProductListQuery } from 'store/slices/rule/rule-entities-enum';
import SuccessSnackbarEffect from 'component/success-snackbar';
import rgba from 'assets/theme/function/rgba';
import borders from 'assets/theme/base/borders';
import { useUserListQuery } from 'store/slices/user/user';
import { UserList } from 'types/user/user';
import FlexibleTable from 'component/flexible-table';
import { RowDataType, TableMetaData } from 'types/common/common';
import { StyledTableRow } from 'component/tabel-pagination-style';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import AddIcon from '@mui/icons-material/Add';
import { API_ENDPOINT_URL } from 'constant';
import CloseIcon from '@mui/icons-material/Close';
import { PresentUserRequest } from 'types/presen-user/present-user';
import { usePresentCardMutation } from 'store/slices/cards/card/present-card-user';
import { useCardTitleListQuery } from 'store/slices/cards/card/card-list';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'img',
        position: 1,
        title: 'Image ',
        isSortable: false,
    },
    {
        key: 'name',
        position: 2,
        title: 'User Name',
        isSortable: false,
    },

    {
        key: 'action',
        position: 3,
        title: 'Action',
        isSortable: false,
    },
];

const SystemPresent: React.FC = () => {
    const [searchInput, setSearchInput] = useState('');
    const [selectedUsers, setSelectedUsers] = useState<UserList[]>([]);
    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<PresentUserRequest>({
        defaultValues: {
            userId: [],
            ruleId: undefined,
            payload: {
                gold: undefined,
                gem: undefined,
                card: undefined,
                comment: undefined,
            },
        },
    });
    const [tableMetaDataList, setTableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [dataUser, setDataUser] = useState<UserNotification>({
        userNotification: [],
    });
    const [sort, setSort] = useState<string>('nickName');
    const [tableData, setTableData] = useState<RowDataType[]>([]);
    const [cardTitle, setCardTitle] = useState('');

    const { data: ruleProductList } = useRuleProductListQuery({
        entity: 'notification',
    });
    const { data: userList, isFetching: userListIsFeatching } =
        useUserListQuery({
            nickname: searchInput,
        });
    const { data: dataCard, isFetching: dataCardIsFeatching } =
        useCardTitleListQuery(cardTitle);

    const [sendPresent, { error, isError, isSuccess, isLoading }] =
        usePresentCardMutation();

    const formRef = useRef<HTMLFormElement>(null);

    const onSubmit = (values: PresentUserRequest) => {
        const userList =
            dataUser?.userNotification.map((user) => user.id) ?? [];

        sendPresent({
            userId: userList,
            ruleId: values.ruleId,
            payload: {
                gem: values.payload?.gem,
                gold: values.payload?.gold,
                card: values.payload?.card,
                comment: values.payload?.comment,
            },
        });
    };

    const addUserData = () => {
        const usersData = selectedUsers.map((u) => ({
            id: u.id,
            name: u.name,
        }));

        setDataUser((prev) => {
            const existingIds = new Set(
                prev.userNotification?.map((u) => u.id) ?? [],
            );
            const newUsers = usersData.filter((u) => !existingIds.has(u.id));

            return {
                ...prev,
                userNotification: [
                    ...(prev.userNotification ?? []),
                    ...newUsers,
                ],
            };
        });

        setSelectedUsers([]);
    };

    const deleteUser = (id: string) => {
        setDataUser((prev) => {
            if (!prev.userNotification) return prev;
            return {
                ...prev,
                userNotification: prev.userNotification.filter(
                    (user) => user.id !== id,
                ),
            };
        });
    };

    const dispatch = useAppDispatch();

    useEffect(() => {
        const tableData: RowDataType[] =
            dataUser.userNotification?.map((fullInfo) => {
                const user = userList?.find((u) => u.id === fullInfo.id);
                return {
                    ...fullInfo,
                    img: user?.selectAvatarId ? (
                        <Box
                            component="img"
                            src={`${API_ENDPOINT_URL}/avatar/file/${user.selectAvatarId}`}
                            alt=""
                            sx={{
                                width: 60,
                                height: 60,
                                objectFit: 'cover',
                                objectPosition: 'top',
                                borderRadius: borders.borderRadius.section,
                            }}
                        />
                    ) : (
                        <Typography variant="h5">-</Typography>
                    ),
                    action: (
                        <Box display="flex" justifyContent="center" gap={2}>
                            <Tooltip title="Delete">
                                <Delete
                                    cursor="pointer"
                                    onClick={() => deleteUser(fullInfo.id)}
                                />
                            </Tooltip>
                        </Box>
                    ),
                    rowComponent: StyledTableRow,
                };
            }) || [];

        setTableData(tableData);
    }, [dataUser.userNotification, userList]);

    useEffect(() => {
        if (isSuccess) {
            reset();
            setTableData([]);
        }
    }, [isSuccess]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    return (
        <>
            <Box
                border={`1px solid ${rgba(colors.info.main, 0.5)}`}
                borderRadius={borders.borderRadius.md}
                p={pxToRem(10)}
                ref={formRef}
                component="form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <Box display={'flex'}>
                    <FormControl fullWidth>
                        <Controller
                            name="payload.comment"
                            control={control}
                            rules={{
                                required: 'Present comment is required',
                            }}
                            render={({ field: { name, value, onChange } }) => (
                                <TextField
                                    fullWidth
                                    name={name}
                                    value={value ?? ''}
                                    onChange={onChange}
                                    label="Comment"
                                />
                            )}
                        />
                    </FormControl>

                    <FormControl
                        error={Boolean(errors?.payload?.card)}
                        fullWidth
                        sx={{ ml: pxToRem(10) }}
                    >
                        <Controller
                            name="payload.card"
                            control={control}
                            rules={{
                                required: 'Card is required',
                            }}
                            render={({ field: { onChange, value, ref } }) => (
                                <Autocomplete
                                    disableClearable
                                    loading={dataCardIsFeatching}
                                    options={dataCard ?? []}
                                    value={
                                        (dataCard ?? []).find(
                                            (card) => card.id === value,
                                        ) ?? undefined
                                    }
                                    onChange={(_, newValue) => {
                                        onChange(newValue ? newValue.id : '');
                                    }}
                                    inputValue={cardTitle}
                                    onInputChange={(_, newInput, reason) => {
                                        if (reason !== 'reset') {
                                            setCardTitle(newInput);
                                        }
                                    }}
                                    getOptionLabel={(option) =>
                                        option.title || ''
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Card"
                                            variant="outlined"
                                            error={Boolean(
                                                errors?.payload?.card,
                                            )}
                                            helperText={errors?.payload?.card?.message?.toString()}
                                            slotProps={{
                                                input: {
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {dataCardIsFeatching && (
                                                                <CircularProgress
                                                                    color="inherit"
                                                                    size={20}
                                                                />
                                                            )}
                                                            {
                                                                params
                                                                    .InputProps
                                                                    .endAdornment
                                                            }
                                                        </>
                                                    ),
                                                },
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                    </FormControl>

                    <FormControl fullWidth sx={{ ml: pxToRem(10) }}>
                        <Controller
                            name="payload.gold"
                            control={control}
                            render={({ field: { name, value, onChange } }) => (
                                <TextField
                                    fullWidth
                                    name={name}
                                    value={value ?? ''}
                                    onChange={onChange}
                                    label="Gold"
                                />
                            )}
                        />
                    </FormControl>
                    <FormControl fullWidth sx={{ ml: pxToRem(10) }}>
                        <Controller
                            name="payload.gem"
                            control={control}
                            render={({ field: { name, value, onChange } }) => (
                                <TextField
                                    fullWidth
                                    name={name}
                                    value={value ?? ''}
                                    onChange={onChange}
                                    label="Gem"
                                />
                            )}
                        />
                    </FormControl>

                    <FormControl fullWidth sx={{ ml: pxToRem(10) }}>
                        <InputLabel>Rule</InputLabel>
                        <Controller
                            name="ruleId"
                            control={control}
                            render={({ field: { name, value, onChange } }) => {
                                return (
                                    <Select
                                        label="Rule"
                                        name={name}
                                        value={value ?? ''}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                        }}
                                        input={
                                            <OutlinedInput
                                                label="Rule"
                                                endAdornment={
                                                    value && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                onChange('')
                                                            }
                                                            sx={{
                                                                position:
                                                                    'absolute',
                                                                top: '50%',
                                                                right: 25,
                                                                transform:
                                                                    'translateY(-50%)',
                                                                zIndex: 1,
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    )
                                                }
                                            />
                                        }
                                    >
                                        {ruleProductList?.map((category) => (
                                            <MenuItem
                                                key={category.id.toString()}
                                                value={category.id.toString()}
                                            >
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                );
                            }}
                        />
                    </FormControl>
                </Box>

                <Box display={'flex'} pt={pxToRem(10)} alignItems={'center'}>
                    <Controller
                        name="userId"
                        control={control}
                        render={({ field: { onChange, ref } }) => (
                            <Autocomplete
                                limitTags={5}
                                disableCloseOnSelect
                                multiple
                                sx={{ width: '50%' }}
                                options={userList ?? []}
                                loading={userListIsFeatching}
                                value={selectedUsers}
                                onChange={(_, newValue) => {
                                    setSelectedUsers(newValue);
                                    onChange(newValue.map((u) => u.id));
                                }}
                                inputValue={searchInput}
                                onInputChange={(_, newInput, reason) => {
                                    if (reason !== 'reset') {
                                        setSearchInput(newInput);
                                    }
                                }}
                                getOptionLabel={(option) => option.name || ''}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                filterSelectedOptions
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        label="Users"
                                        variant="outlined"
                                        fullWidth
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {userListIsFeatching && (
                                                            <CircularProgress
                                                                color="inherit"
                                                                size={20}
                                                            />
                                                        )}
                                                        {
                                                            params.InputProps
                                                                .endAdornment
                                                        }
                                                    </>
                                                ),
                                            },
                                        }}
                                    />
                                )}
                            />
                        )}
                    />
                    <Box sx={{ cursor: 'pointer' }} onClick={addUserData}>
                        <AddIcon fontSize={'large'} />
                    </Box>
                </Box>
            </Box>
            <Box
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
                mt={pxToRem(15)}
            >
                <FlexibleTable
                    tableMetaDataList={tableMetaDataList}
                    setTableMetaDataList={setTableMetaDataList}
                    tableDataList={tableData}
                    sort={sort}
                    setSort={setSort}
                />
            </Box>
            <Box textAlign={'end'} pt={pxToRem(10)}>
                <Button
                    sx={{ width: '10%' }}
                    onClick={() => {
                        dispatch(
                            openConfirmationDialogs({
                                textInfo:
                                    'Are you sure you want to send gifts?',
                                handleConfirm: () =>
                                    formRef.current?.requestSubmit(),
                            }),
                        );
                    }}
                    variant={'contained'}
                    loading={isLoading}
                >
                    Send
                </Button>
            </Box>
            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Gift sent!'}
            />
        </>
    );
};
export default SystemPresent;
